import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { HiArrowRight, HiArrowLeft } from 'react-icons/hi2';
import SafeImage from '@ui/SafeImage';
import { formatCurrency } from '@util/index';
import { SimilarMasterDisplay } from '@c/MasterProductsPageV2';

interface SimilarProductsProps {
  products: SimilarMasterDisplay[];
  brand: string;
  model: string;
}

export default function SimilarProducts({ products, brand, model }: SimilarProductsProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!sectionRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  if (!products || products.length === 0) return null;

  return (
    <section
      id="similar"
      ref={sectionRef}
      aria-label={`Similar to ${brand} ${model}`}
      className="max-w-default mx-auto px-[2rem] md:px-[4rem] xl:px-default-padding-x py-[3rem]"
    >
      <h2 className="text-[2.4rem] sm:text-[3.2rem] font-extrabold text-black mb-[2rem]">
        Similar to {brand} {model}
      </h2>

      {!isVisible && (
        <div className="min-h-[25rem] flex items-center justify-center text-gray-400 text-[1.4rem]">
          Loading similar products...
        </div>
      )}

      {isVisible && (
        <div className="relative">
          <button
            className="similar-prev-btn absolute left-[-2rem] top-1/2 -translate-y-1/2 z-10 h-[4rem] w-[4rem] rounded-full bg-[#242e5f] text-white flex items-center justify-center hover:bg-[#C75008] transition-colors disabled:opacity-0 disabled:pointer-events-none"
            aria-label="Previous similar products"
          >
            <HiArrowLeft className="w-[1.8rem] h-[1.8rem]" />
          </button>

          <button
            className="similar-next-btn absolute right-[-2rem] top-1/2 -translate-y-1/2 z-10 h-[4rem] w-[4rem] rounded-full bg-[#242e5f] text-white flex items-center justify-center hover:bg-[#C75008] transition-colors disabled:opacity-0 disabled:pointer-events-none"
            aria-label="Next similar products"
          >
            <HiArrowRight className="w-[1.8rem] h-[1.8rem]" />
          </button>

          <Swiper
            modules={[Navigation]}
            spaceBetween={16}
            slidesPerView={1}
            navigation={{
              prevEl: '.similar-prev-btn',
              nextEl: '.similar-next-btn',
            }}
            breakpoints={{
              440: { slidesPerView: 2 },
              768: { slidesPerView: 3 },
              1024: { slidesPerView: 4 },
            }}
          >
            {products.map((product) => (
              <SwiperSlide key={product.id}>
                <Link href={`/m/${product.slug}`} className="block group">
                  <div className="bg-white border border-brand-light-gray rounded-[0.5rem] overflow-hidden transition hover:shadow-md h-full flex flex-col">
                    <div className="w-full h-[16rem] relative bg-white overflow-hidden flex-shrink-0">
                      {(product.images?.[0] || product.thumbnail) ? (
                        <SafeImage
                          src={product.images?.[0] || product.thumbnail || ''}
                          alt={product.title || 'Similar product'}
                          fill
                          classes="object-contain"
                          style={{ objectFit: 'contain' }}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-300 text-[1.2rem]">
                          No image
                        </div>
                      )}
                    </div>
                    <div className="p-[0.8rem] flex flex-col flex-grow">
                      <h3 className="text-[1.3rem] font-medium line-clamp-2 min-h-[3.6rem] mb-[0.5rem] group-hover:text-brand-secondary transition-colors">
                        {product.title || 'Untitled Product'}
                      </h3>
                      {product.brand && (
                        <p className="text-[1.1rem] text-gray-500">{product.brand}</p>
                      )}
                      <div className="min-h-[2.2rem] mt-[0.3rem]">
                        {product.price?.msp != null && product.price.msp > 0 && (
                          <p className="text-[1.4rem] font-bold text-[#242e5f]">
                            From {formatCurrency(product.price.msp)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
    </section>
  );
}
